import React, { useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  Text,
  Textarea,
  Checkbox,
  Select,
  Button,
  HStack,
  AlertDialog,
} from "@chakra-ui/react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import {
  useUpdateActivityMutation,
  useDeleteActivityMutation,
} from "@store/api/activities";
import { useToastNotification } from "@hooks";
import { activityTitles } from "@constants/activities";
import { getActivityBgColor } from "@utils/theme/colors";

import ActivityHeader from "./ActivityHeader";
import CollapsibleSection from "./CollapsibleSection";
import DraggableChoice from "./DraggableChoice";

const PollCreationModal = ({
  isOpen,
  onClose,
  activityType = "poll",
  onSave,
  initialData = null, // For editing mode
  onInitiateRemix = null, // Function to initiate remix flow
}) => {
  const [updateActivity] = useUpdateActivityMutation();
  const [deleteActivity] = useDeleteActivityMutation();
  const { showSuccessToast, showErrorToast } = useToastNotification();
  const isEditMode = !!initialData;
  const [createdActivity, setCreatedActivity] = React.useState(null);
  const choicesContainerRef = useRef(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const showDeleteButton = false;

  // Create default options only once to prevent infinite re-renders
  const [defaultChoices] = useState(() => [
    { id: uuidv4(), option: "", correct: false },
    { id: uuidv4(), option: "", correct: false },
  ]);

  const validationSchema = Yup.object({
    title: Yup.string().required("Question is required"),
    options: Yup.array()
      .min(2, "At least 2 choices are required")
      .of(
        Yup.object().shape({
          option: Yup.string().required("Choice text is required"),
        })
      ),
    duration: Yup.number().min(1, "Time limit must be at least 1 minute"),
  });

  const formik = useFormik({
    initialValues: {
      title: initialData?.title || "",
      options: initialData?.options
        ? initialData.options.map((option) => ({
            ...option,
            id: option._id || option.id || uuidv4(),
          }))
        : defaultChoices,
      duration: initialData?.settings?.duration || 5,
      showResult: initialData?.settings?.showResult !== false, // Default to true
      pollType: "single", // Always single for polls
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEditMode) {
          // Update existing activity
          const activityBody = {
            title: values.title,
            options: values.options.map(({ id, ...option }) => ({
              ...option,
              _id: option._id || id, // Use the id as _id if _id doesn't exist
            })),
            settings: {
              duration: parseInt(values.duration),
              showResult: values.showResult,
              pollType: "single", // Always single for polls
            },
          };

          await updateActivity({
            activityId: initialData._id,
            body: activityBody,
          }).unwrap();

          const activityTitle = activityTitles[activityType] || "Activity";
          showSuccessToast(`${activityTitle} updated successfully`);

          // Call onSave callback for edit mode to notify parent component
          await onSave?.(activityBody);
          onClose();
        } else {
          // Create new activity - no transformation needed
          const transformedValues = {
            title: values.title,
            options: values.options,
            settings: {
              duration: parseInt(values.duration),
              pollType: "single", // Always single for polls
              showResult: values.showResult,
            },
          };
          const result = await onSave?.(transformedValues);
          if (result && !isEditMode) {
            setCreatedActivity(result);
            // Reset form after successful creation
            formik.resetForm();
          }
        }
      } catch (error) {
        const activityTitle = activityTitles[activityType] || "Activity";
        const action = isEditMode ? "updating" : "creating";
        showErrorToast(
          error.data?.message || `Error ${action} ${activityTitle}`
        );
        console.error(`Error ${action} activity:`, error);
      }
    },
  });

  const handleChoiceChange = (choiceId, newText) => {
    const updatedOptions = formik.values.options.map((choice) =>
      choice.id === choiceId ? { ...choice, option: newText } : choice
    );
    formik.setFieldValue("options", updatedOptions);
  };

  const handleDeleteChoice = (choiceId) => {
    // Prevent deletion if only 2 options remain
    if (formik.values.options.length <= 2) {
      return;
    }
    const updatedOptions = formik.values.options.filter(
      (choice) => choice.id !== choiceId
    );
    formik.setFieldValue("options", updatedOptions);
  };

  const handleAddChoice = () => {
    const newChoice = {
      id: uuidv4(),
      option: "",
      correct: false,
    };
    formik.setFieldValue("options", [...formik.values.options, newChoice]);

    // Scroll to bottom after adding new choice
    setTimeout(() => {
      if (choicesContainerRef.current) {
        choicesContainerRef.current.scrollTop =
          choicesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleToggleCorrect = (choiceId) => {
    const updatedOptions = formik.values.options.map(
      (choice) =>
        choice.id === choiceId
          ? { ...choice, correct: !choice.correct }
          : { ...choice, correct: false } // Only one correct answer
    );
    formik.setFieldValue("options", updatedOptions);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formik.values.options);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    formik.setFieldValue("options", items);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRemixClick = () => {
    if (onInitiateRemix && initialData) {
      onInitiateRemix(initialData);
    }
  };

  const handleDeleteActivity = async () => {
    try {
      await deleteActivity(initialData._id).unwrap();
      const activityTitle = activityTitles[activityType] || "Activity";
      showSuccessToast(`${activityTitle} deleted successfully`);
      onClose();
    } catch (error) {
      const activityTitle = activityTitles[activityType] || "Activity";
      showErrorToast(error.data?.message || `Error deleting ${activityTitle}`);
      console.error(`Error deleting activity:`, error);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const isQuizMode = activityType === "quiz";

  // Determine the display title based on mode and state
  const getDisplayTitle = () => {
    if (isEditMode && initialData?.displayName) {
      // Edit mode: use existing display name
      return initialData.displayName;
    } else if (createdActivity?.displayName) {
      // Creation complete: use generated display name
      return createdActivity.displayName;
    } else if (formik.isSubmitting) {
      // Currently creating: show loading state
      return "Creating Activity...";
    } else {
      // Initial creation state: show generic title
      return activityType === "quiz" ? "Knowledge Check" : "Poll";
    }
  };

  // Determine the emoji based on mode and state
  const getEmoji = () => {
    if (isEditMode && initialData?.emoji) {
      // Edit mode: use existing emoji
      return initialData.emoji;
    } else if (createdActivity?.emoji) {
      // Creation complete: use generated emoji
      return createdActivity.emoji;
    } else {
      // Default emoji based on activity type
      return activityType === "quiz" ? "📚" : "⏰";
    }
  };

  const title = getDisplayTitle();
  const icon = getEmoji();

  // Get the activity color based on type and current data
  const activityColor = getActivityBgColor(activityType, initialData);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="white"
        borderRadius="14px"
        maxW="424px"
        mx={4}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalBody p={0}>
          <VStack spacing={6} align="stretch" p="14px 14px 16px">
            {/* Header */}
            <ActivityHeader
              icon={icon}
              title={title}
              color={activityColor}
              onCancel={handleCancel}
              onSave={formik.handleSubmit}
              isLoading={formik.isSubmitting}
            />

            {/* Content */}
            <VStack spacing={7} align="stretch">
              {/* Question Section */}
              <VStack spacing={2} align="stretch">
                <Text
                  fontSize="12px"
                  fontWeight="84"
                  lineHeight="1.5"
                  letterSpacing="-0.01em"
                  color="#000000"
                  fontFamily="Geist, sans-serif"
                >
                  Question
                </Text>
                <Textarea
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="How soon can we start this project?"
                  bg="white"
                  border="1px solid #B8B2C0"
                  borderRadius="4px"
                  p={3}
                  size="md"
                  fontSize="12px"
                  fontWeight="84"
                  lineHeight="1.5"
                  letterSpacing="-0.01em"
                  color="#726682"
                  fontFamily="Geist, sans-serif"
                  resize="vertical"
                  _focus={{
                    borderColor: "#4C47CC",
                    boxShadow: "0 0 0 1px #4C47CC",
                  }}
                  data-testid="activity-question-input"
                />
                {formik.touched.title && formik.errors.title && (
                  <Text fontSize="sm" color="red.500">
                    {formik.errors.title}
                  </Text>
                )}
              </VStack>

              {/* Choices Section */}
              <CollapsibleSection title="Choices">
                <VStack spacing={3} align="stretch">
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="choices">
                      {(provided) => (
                        <VStack
                          spacing={3}
                          align="stretch"
                          maxHeight="200px"
                          overflowY="auto"
                          {...provided.droppableProps}
                          ref={(el) => {
                            provided.innerRef(el);
                            choicesContainerRef.current = el;
                          }}
                        >
                          {formik.values.options.map((choice, index) => (
                            <DraggableChoice
                              key={choice.id}
                              choice={choice}
                              index={index}
                              onChange={handleChoiceChange}
                              onDelete={handleDeleteChoice}
                              showCorrectAnswer={isQuizMode}
                              onToggleCorrect={handleToggleCorrect}
                              disableDelete={formik.values.options.length <= 2}
                            />
                          ))}
                          {provided.placeholder}
                        </VStack>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Add Option Button */}
                  <Button
                    onClick={handleAddChoice}
                    bg="white"
                    border="1px solid #FFFFFF"
                    borderRadius="4px"
                    color="#4C47CC"
                    fontSize="12px"
                    fontWeight="84"
                    lineHeight="1.5"
                    letterSpacing="-0.01em"
                    fontFamily="Geist, sans-serif"
                    p={3}
                    h="auto"
                    w="full"
                    textAlign="center"
                    justifyContent="center"
                    _hover={{ bg: "gray.50" }}
                    data-testid="add-quiz-answer-button"
                  >
                    + Add Option
                  </Button>
                </VStack>
              </CollapsibleSection>

              {/* Settings Section */}
              <CollapsibleSection title="Settings">
                <VStack spacing={4} align="stretch">
                  {/* Time Limit */}
                  <HStack spacing={3} align="center">
                    <Text
                      fontSize="12px"
                      fontWeight="84"
                      lineHeight="1.5"
                      letterSpacing="-0.01em"
                      color="#000000"
                      fontFamily="Geist, sans-serif"
                      w="70px"
                    >
                      Time Limit
                    </Text>
                    <Select
                      name="duration"
                      value={formik.values.duration}
                      onChange={formik.handleChange}
                      bg="white"
                      border="1px solid #D0CCD5"
                      borderRadius="4px"
                      fontSize="12px"
                      fontWeight="84"
                      lineHeight="1.5"
                      letterSpacing="-0.01em"
                      color="#0C001C"
                      fontFamily="Geist, sans-serif"
                      w="auto"
                      _focus={{
                        borderColor: "#4C47CC",
                        boxShadow: "0 0 0 1px #4C47CC",
                      }}
                    >
                      <option value={1}>1 min</option>
                      <option value={2}>2 min</option>
                      <option value={3}>3 min</option>
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                      <option value={15}>15 min</option>
                    </Select>
                  </HStack>

                  {/* Show Results Checkbox */}
                  <HStack spacing={2} align="center">
                    <Checkbox
                      name="showResult"
                      isChecked={formik.values.showResult}
                      onChange={(e) =>
                        formik.setFieldValue("showResult", e.target.checked)
                      }
                      colorScheme="purple"
                      size="md"
                    >
                      <Text
                        fontSize="14px"
                        fontWeight="84"
                        lineHeight="1.43"
                        letterSpacing="-0.02em"
                        color="#0C001C"
                        fontFamily="Geist, sans-serif"
                      >
                        Show results to attendees?
                      </Text>
                    </Checkbox>
                  </HStack>
                </VStack>
              </CollapsibleSection>
            </VStack>

            {/* Footer */}
            <VStack spacing={3} align="stretch" pt={2}>
              {/* Remix Button - only show in edit mode */}
              {isEditMode && (
                <Button
                  bg="#4C47CC"
                  color="white"
                  fontSize="13px"
                  fontWeight="700"
                  lineHeight="1.23"
                  fontFamily="Geist, sans-serif"
                  borderRadius="8px"
                  py={4}
                  px={14}
                  h="auto"
                  _hover={{ bg: "#3D39A3" }}
                  _active={{ bg: "#3D39A3" }}
                  onClick={handleRemixClick}
                >
                  ✨ Tired of Typing? Remix it!
                </Button>
              )}

              {/* Footer Links */}
              <HStack justify="space-between" spacing={4}>
                {isEditMode && showDeleteButton && (
                  <Button
                    variant="outline"
                    color="#D92D20"
                    borderColor="#FFFFFF"
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight="1.43"
                    letterSpacing="-0.02em"
                    fontFamily="Geist, sans-serif"
                    textAlign="center"
                    h="24px"
                    px={2}
                    flex={1}
                    _hover={{ bg: "red.50" }}
                    onClick={handleDeleteClick}
                    isLoading={deleteActivity.isLoading}
                  >
                    Delete Activity
                  </Button>
                )}
              </HStack>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        title={`Delete ${activityTitles[activityType] || "Activity"}`}
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        primaryButton={
          <Button
            size="sm"
            colorScheme="red"
            onClick={handleDeleteActivity}
            isLoading={deleteActivity.isLoading}
            ml={3}
          >
            Delete
          </Button>
        }
        secondaryButton={
          <Button
            size="sm"
            onClick={handleDeleteCancel}
            bg="white"
            disabled={deleteActivity.isLoading}
          >
            Cancel
          </Button>
        }
      >
        {`Are you sure you want to delete this ${
          activityTitles[activityType] || "Activity"
        }?`}
      </AlertDialog>
    </Modal>
  );
};

export default PollCreationModal;
